import moment from "moment";
import { useTranslation } from "react-i18next";
import React from "react";
import styled from "styled-components";
import { DocValidationUtils } from "../../doc-validation/doc-validation.utils";
import { renderDate } from "../../../../shared/shared.utils";
import { IClientDocTaskHistoryEntry } from "../doc-task-history.model";
import { IIdentityDocument } from "../../../../shared/models/identity.model";
import Occupations from "../../../../shared/utils/occupations";
import { RISK_RATINGS } from "../../../../shared/constants/Nationalities";

interface IDocTaskHistoryListProps {
  changeHistories?: IClientDocTaskHistoryEntry[];
  identities?: IIdentityDocument[];
}

const DocTaskHistoryList: React.FC<IDocTaskHistoryListProps> = ({
  changeHistories,
  identities,
}: IDocTaskHistoryListProps) => {
  const { t, i18n } = useTranslation();
  const formatFieldName = (field: string) => {
    return field.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, str => str.toUpperCase());
  };

  const isFrench = i18n.language.toLowerCase() === "fr";

  const getOccupationLabel = (occupationKey: string) => {
    const occupation = Occupations[occupationKey as keyof typeof Occupations];
    if (!occupation) {
      return occupationKey;
    }
    return isFrench ? occupation.FR : occupation.EN;
  };

  const getRiskLabel = (riskKey: string) => {
    const numericKey = Number(riskKey);
    const matched = RISK_RATINGS.find(r => r.id === numericKey);
    if (!matched) {
      return riskKey;
    }
    return t(matched.key, matched.description);
  };

  const getDocumentLabel = (docType: string) => {
    const matchedDoc = identities?.find(d => d.documentId === docType);
    if (!matchedDoc) {
      return docType;
    }
    return isFrench ? matchedDoc.fr : matchedDoc.en;
  };

  const getGenderLabel = (genderKey: string | number | undefined) => {
    if (genderKey === undefined || genderKey === null || genderKey === "") {
      return "";
    }
    const key = String(genderKey);
    return t(`market_values:genders.${key}`);
  };

  const getFormattedValue = (field: string, value: string): string => {
    if (field === "documentType") {
      return getDocumentLabel(value);
    }

    if (field === "occupation") {
      return getOccupationLabel(value);
    }

    if (field === "riskRating") {
      return getRiskLabel(value);
    }

    if (field === "nationality") {
      return t(`market_values:nationalities.${value}`, t("commons.unknownValue"));
    }

    if (field === "gender") {
      return getGenderLabel(value);
    }

    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      return moment(value).format("YYYY-MM-DD");
    }
    return value;
  };

  return (
    <ListContainer>
      {(changeHistories ?? []).map((entry, index) => (
        <HistoryCard key={index}>
          {/* Left Section */}
          <HistoryDetails>
            <TextLabel>
              <strong>Operator :</strong> <GrayText>{entry.operator}</GrayText>
            </TextLabel>
            <TextLabel>
              <strong>Date created : </strong>
              <GrayText>{moment(entry.statusDate).format("YYYY-MM-DD HH:mm:ss")}</GrayText>
            </TextLabel>
            <TextLabel>
              <strong>Status :</strong> <GrayText>{entry.status}</GrayText>
            </TextLabel>
            <TextLabel>
              <strong>Approver :</strong> <GrayText>{entry.approver}</GrayText>
            </TextLabel>
            <TextLabel>
              <strong>Update date : </strong>
              <GrayText>
                {entry.dateUpdated ? moment(entry.dateUpdated).format("YYYY-MM-DD HH:mm:ss") : "Not yet updated"}
              </GrayText>
            </TextLabel>
          </HistoryDetails>

          {/* Separator Line */}
          <Separator />
          {/* Right Section */}
          <ChangesSection>
            <TextLabel>
              <strong>Fields changed</strong>
            </TextLabel>
            {(entry.changes ?? []).map((change, i) => (
              <TextLabel key={i}>
                <strong>{formatFieldName(change.field)} :</strong>
                <OldValue>{getFormattedValue(change.field, String(change.oldValue))}</OldValue>
                <NewValue>{getFormattedValue(change.field, String(change.newValue))}</NewValue>
              </TextLabel>
            ))}
          </ChangesSection>
        </HistoryCard>
      ))}
    </ListContainer>
  );
};

export default DocTaskHistoryList;

const ListContainer = styled("div")`
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  font-family: Vodafone Rg;
`;

const HistoryCard = styled("div")`
  display: flex;
  background: #fff;
  padding: 15px;
  border-radius: 5px;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.1);
  width: 500px;
  align-items: flex-start;
  font-family: Vodafone Rg;
`;

const HistoryDetails = styled("div")`
  width: 50%;
  padding-right: 10px;
  font-family: Vodafone Rg;
`;

const Separator = styled("div")`
  width: 2px;
  background: #ccc;
  height: 100%;
  margin: 0 10px;
  font-family: Vodafone Rg;
`;

const ChangesSection = styled("div")`
  width: 50%;
`;

const TextLabel = styled("div")`
  color: black;
  margin-bottom: 5px;
  font-size: 14px;
  font-family: Vodafone Rg;
`;

const GrayText = styled("span")`
  color: gray;
  font-family: Vodafone Rg;
`;

const OldValue = styled("span")`
  color: red;
  text-decoration: line-through;
  margin-left: 5px;
  font-family: Vodafone Rg;
`;

const NewValue = styled("span")`
  color: green;
  margin-left: 5px;
  font-family: Vodafone Rg;
`;
